import { useMemo, useState } from "react";
import api, { fmtIDR, apiErr } from "@/lib/api";
import { fmtDate } from "@/components/DateInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Link2, Unlink, Loader2, Search } from "lucide-react";

export function FakturLink({ r, kind, reload }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const doc = kind === "masukan" ? "transaksi" : "invoice";
  const load = async () => {
    setOpen(true); setData(null); setQ("");
    try { setData((await api.get(`/faktur/${r.id}/candidates`)).data); } catch (e) { toast.error(apiErr(e.response?.data?.detail)); setOpen(false); }
  };
  const save = async (d) => {
    try {
      await api.put(`/faktur/${r.id}/link`, d ? { match_type: d.type, match_id: d.id } : { match_id: null });
      toast.success(d ? `Faktur ditautkan ke ${d.ref || doc}` : "Tautan manual dilepas"); setOpen(false); reload();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const rows = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return (data?.rows || []).filter((d) => !ql || `${d.ref} ${d.party} ${d.ppn} ${fmtDate(d.date)}`.toLowerCase().includes(ql));
  }, [data, q]);
  const manual = r.match_by === "manual" || r.match_by === "manual_missing";
  return (
    <>
      <div className="flex gap-1 mt-0.5">
        <button type="button" onClick={load} className="inline-flex items-center gap-0.5 text-[10px] font-medium text-primary hover:underline" data-testid={`faktur-link-${r.id}`}>
          <Link2 className="h-3 w-3" />{r.match_id ? "Ganti" : "Tautkan manual"}</button>
        {manual && <button type="button" onClick={() => save(null)} className="inline-flex items-center gap-0.5 text-[10px] text-rose-600 hover:underline" data-testid={`faktur-unlink-${r.id}`}>
          <Unlink className="h-3 w-3" />Lepas</button>}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto">
          <DialogHeader><DialogTitle>Tautkan Faktur {r.nomor_faktur}</DialogTitle>
            <DialogDescription className="text-xs">{r.nama || "—"} · PPN {fmtIDR(r.ppn)} · masa {r.masa}/{r.tahun}. Pilih {doc} ber-PPN periode {data ? `${fmtDate(data.from)} – ${fmtDate(data.to)}` : "masa ±1 bulan"}; diurutkan dari selisih PPN terkecil.</DialogDescription></DialogHeader>
          <div className="relative"><Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nomor, nama, nilai PPN, tanggal…" className="pl-9 h-9" data-testid="faktur-link-search" /></div>
          {!data ? <div className="py-8 text-center"><Loader2 className="inline h-4 w-4 animate-spin" /></div> : (
            <div className="overflow-auto rounded-md border border-border max-h-96">
              <Table>
                <TableHeader><TableRow className="bg-secondary/50"><TableHead>Tanggal</TableHead><TableHead>Referensi</TableHead><TableHead>{kind === "masukan" ? "Vendor" : "Pelanggan"}</TableHead>
                  <TableHead className="text-right">PPN</TableHead><TableHead className="text-right">Selisih</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {rows.length === 0 && <TableRow><TableCell colSpan={6} className="py-8 text-center text-sm text-muted-foreground">Tidak ada {doc} ber-PPN pada periode ini.</TableCell></TableRow>}
                  {rows.map((d) => (
                    <TableRow key={d.id} data-testid={`faktur-cand-${d.id}`} className={d.id === data.current ? "bg-primary/5" : ""}>
                      <TableCell className="num text-xs">{fmtDate(d.date)}</TableCell><TableCell className="text-xs">{d.ref || "—"}</TableCell>
                      <TableCell className="text-xs">{d.party || "—"}{d.linked_to && <span className="block text-[10px] text-amber-700">sudah ditautkan ke {d.linked_to}</span>}</TableCell>
                      <TableCell className="num text-right text-xs">{fmtIDR(d.ppn)}</TableCell>
                      <TableCell className={`num text-right text-xs ${Math.abs(d.diff) <= 1 ? "text-emerald-700" : "text-amber-700"}`}>{fmtIDR(d.diff)}</TableCell>
                      <TableCell><Button size="sm" variant={d.id === data.current ? "secondary" : "outline"} className="h-7 text-xs" disabled={d.id === data.current}
                        onClick={() => save(d)} data-testid={`faktur-cand-pick-${d.id}`}>{d.id === data.current ? "Tertaut" : "Pilih"}</Button></TableCell>
                    </TableRow>))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
